import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

interface Product {
  id: number | string;
  name: string;
  category: string;
  price: number;
  unit: string;
  imageURL: string;
  isFeatured?: boolean;
  origin?: string;
  description?: string;
}

interface ProductCardProps {
  product: Product;
}

const ProductCard = ({ product }: ProductCardProps) => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [showDescription, setShowDescription] = useState(false);

  const handleAddToCart = () => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: 1,
      unit: product.unit,
      imageURL: product.imageURL
    });
    // Redirect to checkout immediately after adding to cart
    navigate('/checkout');
  };

  return (
    <div className="group relative bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300">
      {/* Image Container */}
      <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden bg-gray-200 xl:aspect-w-7 xl:aspect-h-8">
        <img
          src={product.imageURL}
          alt={product.name}
          className="h-64 w-full object-cover object-center group-hover:opacity-90 transition duration-300"
        />
        {product.isFeatured && (
          <span className="absolute top-2 left-2 bg-accent text-white text-xs font-bold px-2 py-1 uppercase tracking-wider">
            Premium
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="mt-1 text-lg font-serif font-bold text-primary truncate">
          {product.name}
        </h3>
        <p className="mt-1 text-sm text-gray-500 font-sans">{product.category} {product.origin && `• ${product.origin}`}</p>
        
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xl font-medium text-primary">
            ₦{product.price.toLocaleString()} <span className="text-sm text-gray-400">/ {product.unit}</span>
          </p>
          
          {/* Info Icon for Description */}
          <button 
            onClick={() => setShowDescription(!showDescription)}
            className="text-gray-400 hover:text-primary transition focus:outline-none"
            title="View Description"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>

        {/* Description Panel */}
        {showDescription && (
          <div className="mt-3 mb-3 p-2 bg-gray-50 rounded text-sm text-gray-600 border border-gray-100">
            {product.description || "No description available."}
          </div>
        )}

        <div className="mt-4">
          <button 
            onClick={handleAddToCart}
            className="w-full bg-primary text-secondary px-4 py-2 rounded-none hover:bg-green-800 transition font-sans text-sm font-semibold"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;